import { useEffect, useState, FormEvent } from "react";
import { useParams } from "react-router-dom";

const BASE_URL = (import.meta as any).env?.VITE_API_URL ?? "";

interface VesselInfo {
  id: string;
  name: string;
  contact_email: string | null;
}

interface ShoreMessage {
  id: string;
  subject: string;
  body: string;
  created_at: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isAutoSubject(subject: string): boolean {
  return subject.startsWith("Shore message —") || subject.startsWith("Shore message -");
}

export default function VesselPortalPage() {
  const { vesselId } = useParams<{ vesselId: string }>();

  const [vessel, setVessel] = useState<VesselInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [shoreMessages, setShoreMessages] = useState<ShoreMessage[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form fields — pre-filled once vessel loads
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState<"normal" | "urgent">("normal");

  useEffect(() => {
    if (!vesselId) return;

    // Dedicated /info endpoint for pre-fill data
    fetch(`${BASE_URL}/public/vessel/${vesselId}/info`)
      .then((r) => r.json())
      .then((data: any) => {
        if (data.error) {
          setLoadError("Vessel not found");
          return;
        }
        const v = data as VesselInfo;
        setVessel(v);
        setSenderName(`Master, ${v.name}`);
        if (v.contact_email) setSenderEmail(v.contact_email);
      })
      .catch(() => setLoadError("Could not load vessel information"));

    fetch(`${BASE_URL}/public/vessel/${vesselId}/messages`)
      .then((r) => r.json())
      .then((data: any) => {
        if (Array.isArray(data)) setShoreMessages(data);
      })
      .catch(() => {});
  }, [vesselId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);

    try {
      const res = await fetch(`${BASE_URL}/public/vessel/${vesselId}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          senderName,
          senderEmail: senderEmail || undefined,
          body,
          priority,
        }),
      });

      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json().catch(() => ({ error: "Send failed" }));
        setSubmitError((data as { error: string }).error);
      }
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-navy-950 mb-4">SeaVault</h1>
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 py-10 px-4">
      <div className="max-w-lg mx-auto">

        {/* Header */}
        <div className="mb-6 text-center">
          <p className="text-xs font-semibold tracking-widest text-navy-300 uppercase mb-1">SeaVault</p>
          <h1 className="text-xl font-bold text-white">
            {vessel ? vessel.name : "Loading…"}
          </h1>
          <p className="text-sm text-navy-400 mt-0.5">Vessel communications portal</p>
        </div>

        {/* Shore messages */}
        {shoreMessages.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xs font-semibold tracking-wider text-navy-300 uppercase mb-3">
              Messages from shore office
            </h2>
            <div className="space-y-2">
              {shoreMessages.map((m) => {
                const isExpanded = expandedId === m.id;
                // Body preview: first ~80 chars
                const preview = m.body.length > 80 ? m.body.slice(0, 80).trimEnd() + "…" : m.body;
                return (
                  <div
                    key={m.id}
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                    className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 cursor-pointer hover:bg-white/15 transition-colors select-none"
                  >
                    {/* Collapsed row */}
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-white leading-snug flex-1">
                        {isExpanded ? (
                          <span className="font-medium text-navy-200">
                            {isAutoSubject(m.subject) ? "Shore office" : m.subject}
                          </span>
                        ) : (
                          preview
                        )}
                      </p>
                      <div className="shrink-0 flex items-center gap-2">
                        <span className="text-xs text-navy-400">{formatDate(m.created_at)}</span>
                        <svg
                          className={`w-4 h-4 text-navy-300 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Expanded body */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-white/20">
                        {!isAutoSubject(m.subject) && (
                          <p className="text-xs text-navy-300 font-medium mb-2">{m.subject}</p>
                        )}
                        <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
                          {m.body}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Reply / compose card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {sent ? (
            <div className="text-center py-4">
              <div className="text-3xl mb-3">✓</div>
              <p className="font-semibold text-gray-800 mb-1">Message sent</p>
              <p className="text-sm text-gray-500">The shore team has been notified.</p>
              <button
                onClick={() => { setSent(false); setBody(""); }}
                className="mt-4 text-sm text-navy-600 hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-800">
                {shoreMessages.length > 0 ? "Reply to shore office" : "Message shore office"}
              </h2>

              {submitError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              {/* Sender name — pre-filled, editable */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                  Your name
                </label>
                <input
                  required
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Master, Vessel Name"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                />
              </div>

              {/* Message body */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type your message here…"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none"
                />
              </div>

              {/* Urgent + Send row */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={priority === "urgent"}
                    onChange={(e) => setPriority(e.target.checked ? "urgent" : "normal")}
                    className="accent-red-600 h-4 w-4"
                  />
                  <span className={priority === "urgent" ? "text-red-600 font-semibold" : "text-gray-600"}>
                    Urgent
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting || !body.trim()}
                  className="rounded-lg bg-navy-700 px-6 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
